
import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import {
    TextField, Button, Grid, Typography, Container, Paper, Box, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Autocomplete, Checkbox, FormControlLabel
} from '@mui/material';
import Navbar from "../../../../globals/components/Navbar";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { apiClient } from "../../../../globals/utils/api";

const NewParalegal = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sendInvite, setSendInvite] = useState(false);

    // Dropdown Data
    const [lawFirms, setLawFirms] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const [formData, setFormData] = useState({
        lawFirmId: "",
        locationId: "",

        prefix: "",
        firstName: "",
        middleName: "",
        lastName: "",
        suffix: "",
        email: "",
        useFirmEmail: false,
        firmEmail: "",

        phoneNumber: "",
        extension: "",

        // Fixed for this page
        position: "Paralegal",
        isLegalService: true,
        permissionLevel: "User"
    });

    useEffect(() => {
        const fetchLawFirms = async () => {
            try {
                const response = await apiClient("/list-lawfirms/", "GET");
                setLawFirms(response);
            } catch (error) {
                console.error("Error fetching law firms:", error);
                toast.error("Failed to load law firms");
            }
        };
        fetchLawFirms();
    }, []);

    const handleFirmChange = async (firmId) => {
        setFormData(prev => ({ ...prev, lawFirmId: firmId, locationId: "" }));
        if (!firmId) {
            setLocations([]);
            return;
        }

        setLoadingLocations(true);
        try {
            const response = await apiClient(`/get-locations-by-lawfirm/${firmId}`, "GET");
            setLocations(response);
        } catch (error) {
            console.error("Error fetching locations:", error);
            toast.error("Failed to load locations");
        } finally {
            setLoadingLocations(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        const payload = {
            user: {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
            },
            law_firm_location_id: formData.locationId,
            lawfirm_id: formData.lawFirmId,

            title: formData.position,
            position: formData.position,

            prefix: formData.prefix,
            middle_name: formData.middleName,
            suffix: formData.suffix,

            phone_number: formData.phoneNumber,
            extension: formData.extension,

            preferred_name: [formData.prefix, formData.firstName, formData.middleName, formData.lastName, formData.suffix].filter(Boolean).join(" "),

            is_legal_service: true, // Always true for this flow as per request
            is_active: true,
            is_admin: formData.permissionLevel === "Admin",
            send_invite: sendInvite,
            firm_email: formData.useFirmEmail ? formData.firmEmail : null,
        };

        try {
            // Using invite-lawfirm-user as requested
            await apiClient("/invite-lawfirm-user", "POST", payload);
            toast.success(
                sendInvite
                    ? "Paralegal created and invite sent successfully"
                    : "Paralegal created successfully"
            );
            navigate(-1);
        } catch (error) {
            console.error(error);
            let errorMsg = "Error creating paralegal";
            if (error.detail) errorMsg = error.detail;
            else if (error.email) errorMsg = `Email: ${error.email}`;
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
                <Paper elevation={3} style={{ padding: '2rem', backgroundColor: '#f5f5f5', borderRadius: '15px' }}>
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: '20px' }}>
                        Create New Paralegal
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>

                            {/* Firm & Location Selection */}
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={lawFirms}
                                    getOptionLabel={(option) => option.name || ""}
                                    value={lawFirms.find(f => f.lawfirm_id === formData.lawFirmId) || null}
                                    onChange={(event, newValue) => handleFirmChange(newValue ? newValue.lawfirm_id : "")}
                                    renderInput={(params) => <TextField {...params} label="Law Firm" required margin="none" />}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required disabled={!formData.lawFirmId || loadingLocations}>
                                    <InputLabel>Location</InputLabel>
                                    <Select
                                        value={formData.locationId}
                                        label="Location"
                                        onChange={(e) => handleInputChange({ target: { name: 'locationId', value: e.target.value } })}
                                        name="locationId"
                                    >
                                        {locations.map((loc) => (
                                            <MenuItem key={loc.law_firm_location_id} value={loc.law_firm_location_id}>
                                                {loc.name} - {loc.city}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ my: 1, borderBottom: '1px solid #e0e0e0' }} />
                                <Typography variant="h6" sx={{ marginBottom: 2 }}>1. User Details</Typography>
                            </Grid>

                            {/* Name Fields */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Prefix"
                                    name="prefix"
                                    value={formData.prefix}
                                    onChange={handleInputChange}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Middle Name"
                                    name="middleName"
                                    value={formData.middleName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Last Name"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Suffix</InputLabel>
                                    <Select
                                        name="suffix"
                                        value={formData.suffix}
                                        label="Suffix"
                                        onChange={handleInputChange}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        <MenuItem value="Jr.">Jr.</MenuItem>
                                        <MenuItem value="Sr.">Sr.</MenuItem>
                                        <MenuItem value="II">II</MenuItem>
                                        <MenuItem value="III">III</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Position - Read Only/Fixed */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Position"
                                    value="Paralegal"
                                    fullWidth
                                    disabled
                                    variant="outlined"
                                />
                            </Grid>

                            {/* Permission Level */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Permission Level</InputLabel>
                                    <Select
                                        name="permissionLevel"
                                        value={formData.permissionLevel}
                                        label="Permission Level"
                                        onChange={handleInputChange}
                                    >
                                        <MenuItem value="Admin">Admin</MenuItem>
                                        <MenuItem value="User">User</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Is this individual an Independent Paralegal who works for multiple law firms?"
                                    value="Yes"
                                    fullWidth
                                    disabled
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    type="email"
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="useFirmEmail"
                                            checked={formData.useFirmEmail}
                                            onChange={handleInputChange}
                                        />
                                    }
                                    label="Use a different email for this firm's communications"
                                />
                            </Grid>

                            {formData.useFirmEmail && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Firm-Specific Email"
                                        name="firmEmail"
                                        value={formData.firmEmail}
                                        onChange={handleInputChange}
                                        fullWidth
                                        type="email"
                                        variant="outlined"
                                        helperText="This email will be used for communications related to this firm only. Login will still use the primary email above."
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6}>
                                <InputMask
                                    mask="(999) 999-9999"
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                >
                                    {(inputProps) => (
                                        <TextField
                                            {...inputProps}
                                            label="Phone Number"
                                            fullWidth
                                            name="phoneNumber"
                                            variant="outlined"
                                        />
                                    )}
                                </InputMask>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Extension"
                                    name="extension"
                                    value={formData.extension}
                                    onChange={handleInputChange}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={sendInvite}
                                            onChange={(e) => setSendInvite(e.target.checked)}
                                        />
                                    }
                                    label="Send Invite Email"
                                />
                            </Grid>

                            {/* Actions */}
                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                                <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} style={{ marginRight: '1rem' }}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                                    {loading ? <CircularProgress size={24} /> : "Create Paralegal"}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Paper>
            </Container>
        </>
    );
};

export default NewParalegal;
